import { MAKRKETPLACE_IMAGE_MAP } from '@/constants/marketplaceImage';
import type { Brand } from '@/types/BrandOffer.types';

export interface BrandDiscoveryResponse {
  'All Brands'?: BrandDiscoveryItem[];
  results?: BrandDiscoveryItem[];
  data?: BrandDiscoveryItem[];
}

export interface BrandDiscoveryItem {
  id: number;
  brandName?: string;
  brandLogoUrl?: string | null;
  name?: string;
  logo?: string | null;
}

/** Curated marketplace brands: logos from `public/images/brands` (see {@link MAKRKETPLACE_IMAGE_MAP}). */
const LOCAL_BRAND_DISCOVERY_ENTRIES: readonly Brand[] = [
  { id: 4834, name: 'Ajio', logo: MAKRKETPLACE_IMAGE_MAP.Ajio },
  { id: 10453, name: 'Nykaa', logo: MAKRKETPLACE_IMAGE_MAP.Nykaa },
  { id: 10454, name: 'Myntra', logo: MAKRKETPLACE_IMAGE_MAP.Myntra },
  { id: 10455, name: 'Myntra FWD', logo: MAKRKETPLACE_IMAGE_MAP['Myntra FWD'] },
  { id: 10456, name: 'Flipkart', logo: MAKRKETPLACE_IMAGE_MAP.Flipkart },
  {
    id: 10457,
    name: 'Flipkart - Spoyl',
    logo: MAKRKETPLACE_IMAGE_MAP.Flipkart,
  },
  { id: 10458, name: 'Shopsy', logo: MAKRKETPLACE_IMAGE_MAP.Shopsy },
  { id: 10459, name: 'Amazon', logo: MAKRKETPLACE_IMAGE_MAP.Amazon },
  { id: 7732, name: 'Meesho', logo: MAKRKETPLACE_IMAGE_MAP.Meesho },
];

function normalizeBrand(item: BrandDiscoveryItem): Brand {
  return {
    id: item.id,
    name: item.name ?? item.brandName ?? '',
    logo: item.logo ?? item.brandLogoUrl ?? null,
  };
}

/**
 * Merges API brands with curated local entries: known IDs get display names + curated `/images/brands/` logos;
 * any curated ID missing from the API is appended.
 */
function mergeLocalBrandDiscovery(brands: Brand[]): Brand[] {
  const byId = new Map<number, Brand>(brands.map((b) => [b.id, b]));
  for (const entry of LOCAL_BRAND_DISCOVERY_ENTRIES) {
    byId.set(entry.id, { ...entry });
  }
  return Array.from(byId.values());
}

export function parseBrandDiscoveryResponse(
  data: BrandDiscoveryResponse | BrandDiscoveryItem[]
): Brand[] {
  let brands: Brand[];

  if (Array.isArray(data)) {
    brands = data.map(normalizeBrand);
  } else {
    const allBrands = data['All Brands'];
    if (Array.isArray(allBrands)) {
      brands = allBrands.map(normalizeBrand);
    } else {
      const fallback = data.results ?? data.data ?? [];
      brands = fallback.map(normalizeBrand);
    }
  }

  return mergeLocalBrandDiscovery(brands);
}
